import React from "react";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

const SHORT_CODE = { en: "EN", fr: "FR" };

const LANGUAGE_LABEL = {
  en: { en: "English", fr: "anglais" },
  fr: { en: "French", fr: "français" },
};

/**
 * Always-visible document-language badge on the Analysis page (compact
 * "EN"/"FR" code, matching the LanguageSwitcher convention).
 *
 * The analysis prose (summary, clauses, risks…) is ALWAYS generated in the
 * user's locale at analysis time — this badge is about the SOURCE PDF's
 * language, not the analysis language. Two visual states:
 *  - Matches the user's UI locale: neutral zinc, purely informational.
 *  - Differs from the user's UI locale: blue informational, since quoted
 *    passages/citations will appear in the source language for fidelity.
 *
 * Returns null when source_language is null/unknown (not yet detected).
 */
export default function SourceLanguageBadge({ sourceLanguage }) {
  const { t, i18n } = useTranslation("analysis");
  const userLocale = i18n.language?.split("-")[0] === "fr" ? "fr" : "en";
  if (sourceLanguage !== "en" && sourceLanguage !== "fr") return null;

  const matchesLocale = sourceLanguage === userLocale;
  const langName = LANGUAGE_LABEL[sourceLanguage]?.[userLocale] || sourceLanguage;
  const tooltip = matchesLocale
    ? t("source_language_badge.tooltip_match", { language: langName })
    : t("source_language_badge.tooltip_diff", { language: langName });

  const styles = matchesLocale
    ? { border: "border-zinc-200", bg: "bg-zinc-100", text: "text-zinc-700", icon: "text-zinc-500" }
    : { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-800", icon: "text-blue-600" };

  return (
    <span
      data-testid="source-language-badge"
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex items-center gap-1.5 rounded-full border ${styles.border} ${styles.bg} px-2.5 py-1 text-xs font-medium ${styles.text}`}
    >
      <Globe size={13} strokeWidth={1.75} className={styles.icon} />
      {SHORT_CODE[sourceLanguage]}
    </span>
  );
}
